import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showNotificationDialog, isEmptyObject } from '../utils';
import { TIMEOUT_DURATION, localStorageKeys } from '../utils/Constants';
import { formatReservationUserInfo } from '../utils/calculationUtils';
import useR05Api from '../api/useR05Api';
import { ErrorType } from 'types/errors';
import { Calendar, Holidays, Stocks, OrderTicket } from 'types';
import { ComboItem } from '@shared/order/get/types';

/**
 *  予約機能の部分
 */
const useReservationTicket = () => {
    const [reservationTicketList, setReservationTicketList] = useState<Stocks>([]);
    const [reservationCalendar, setReservationCalendar] = useState<Calendar>([]);
    const [holidays, setHolidays] = useState<Holidays[]>([]);
    const [isLoading, setLoading] = useState(false);
    const { t } = useTranslation();
    const {
        fetchReservationTicketList,
        fetchReservationCalendar: fetchCalendar,
        fetchHolidays: getHolidays,
        fetchTicketInfo,
    } = useR05Api();
    const activeFetches = useRef(0);

    const startLoading = useCallback(() => {
        activeFetches.current += 1;
        setLoading(true);
    }, [activeFetches]);

    const stopLoading = useCallback(() => {
        activeFetches.current = Math.max(activeFetches.current - 1, 0);
        if (activeFetches.current === 0) {
            setLoading(false);
        }
    }, [activeFetches]);

    /**
     * 在庫一覧の取得に失敗した時のトーストメッセージを表示する
     */
    const showFetchReservationTicketError = useCallback(
        (error: ErrorType) => {
            const showError = (text: string) => {
                showNotificationDialog('error', 'fetchTicketInfo', t('notification.title'), text);
            };
            if (error.isTimeout) {
                showError(t('error-message.server-error'));
                return;
            }
            switch (error.errorType) {
                case 'offline':
                    showError(t('error-message.server-error'));
                    break;
                case 'undefined_parameter':
                case 'not_found_ticket':
                case 'invalid_api_key':
                    showError(t('error-message.tickets.fetch-info-failed'));
                    break;
                case 'unexpected':
                default:
                    showError(t('error-message.tickets.unexpected-error'));
                    break;
            }
        },
        [t],
    );

    // 在庫一覧の取得
    const fetchReservationTickets = useCallback(
        async (params: {
            saleCombinationTicketId: string;
            year: number;
            month: number;
            day: number;
            startTargetReserveOn: Date | null;
            endTargetReserveOn: Date | null;
        }) => {
            startLoading();
            try {
                if (!navigator.onLine) {
                    stopLoading();
                    showFetchReservationTicketError({ errorType: 'offline' });
                    return;
                }
                const response = (await fetchReservationTicketList(params, TIMEOUT_DURATION)) ?? [];
                setReservationTicketList(response);
            } catch (error: any) {
                showFetchReservationTicketError(error);
            } finally {
                stopLoading();
            }
        },
        [fetchReservationTicketList, showFetchReservationTicketError, stopLoading, startLoading],
    );

    // 予約カレンダー取得
    const fetchReservationCalendar = useCallback(
        async (params: {
            saleCombinationTicketId: string;
            year: number;
            month: number;
            startTargetReserveOn: Date | null;
            endTargetReserveOn: Date | null;
        }) => {
            startLoading();
            try {
                if (!navigator.onLine) {
                    stopLoading();
                    showFetchReservationTicketError({ errorType: 'offline' });
                    return;
                }
                const response = (await fetchCalendar(params, TIMEOUT_DURATION)) ?? [];
                setReservationCalendar(response);
            } catch (error: any) {
                showFetchReservationTicketError(error);
            } finally {
                stopLoading();
            }
        },
        [fetchCalendar, showFetchReservationTicketError, stopLoading, startLoading],
    );

    // 予約カレンダー取得
    const fetchHolidays = useCallback(async () => {
        startLoading();
        try {
            if (!navigator.onLine) {
                stopLoading();
                showFetchReservationTicketError({ errorType: 'offline' });
                return;
            }
            const response = (await getHolidays(TIMEOUT_DURATION)) ?? [];
            setHolidays(response);
        } catch (error: any) {
            showFetchReservationTicketError(error);
        } finally {
            stopLoading();
        }
    }, [getHolidays, showFetchReservationTicketError, startLoading, stopLoading]);

    /**
     * 予約編集用のデータを準備してlocalStorageに保存する
     * @param orderTicket - 予約チケット情報
     * @returns {Promise<boolean>} - 成功時はtrue、失敗時はfalse
     */
    const prepareReservationEdit = useCallback(
        async (orderTicket: OrderTicket) => {
            try {
                setLoading(true);
                const ticketInfo = await fetchTicketInfo({ salesTicketId: String(orderTicket?.saleTicketId) });
                if (isEmptyObject(ticketInfo)) {
                    return false;
                }

                const selectNumber = {
                    adult: orderTicket?.numberAdults,
                    custom1: orderTicket?.numberCustom1,
                    custom2: orderTicket?.numberCustom2,
                    custom3: orderTicket?.numberCustom3,
                };

                const reservedTicketIdsSet = new Set<number>();
                const orderTicketMap = new Map<number, ComboItem>();

                for (const item of orderTicket?.comboItems ?? []) {
                    if (item.isReserve) {
                        reservedTicketIdsSet.add(item.ticketId);
                        orderTicketMap.set(item.ticketId, item);
                    }
                }

                const firstReservationItem = orderTicket?.comboItems?.find((item) => item.isReserve);
                const reservationInfo = firstReservationItem?.orderUsageTickets?.[0]?.reservationInfo;

                const { listFields, userForm } = formatReservationUserInfo(reservationInfo);

                const reservationCombo =
                    ticketInfo?.comboItems
                        ?.filter((item) => item.isReserve && reservedTicketIdsSet.has(item.ticketId))
                        ?.map((buyComboItem) => {
                            const boughtComboItem = orderTicketMap.get(buyComboItem.ticketId);
                            const reservationInfo = boughtComboItem?.orderUsageTickets?.[0]?.reservationInfo;

                            return {
                                ...boughtComboItem,
                                ...buyComboItem,
                                reservationInfo: {
                                    ...reservationInfo,
                                    originStop: {
                                        value: reservationInfo?.originStopId,
                                        shortName: reservationInfo?.originStopName,
                                    },
                                    destStop: {
                                        value: reservationInfo?.destStopId,
                                        shortName: reservationInfo?.destStopName,
                                    },
                                    initValue: {
                                        oldReservationInfo: { ...reservationInfo },
                                        oldUserInfo: {
                                            listFields,
                                            formFields: userForm,
                                        },
                                    },
                                },
                            };
                        }) ?? [];

                const reservationInfoData = {
                    ticketInfo: ticketInfo,
                    selectNumber: selectNumber,
                    reservationCombo: reservationCombo,
                    userInfo: {
                        listFields: listFields,
                        formFields: userForm,
                    },
                    isEdit: true,
                    orderId: orderTicket?.orderId,
                };
                localStorage.setItem(localStorageKeys.RESERVATION_INFO_DATA, JSON.stringify(reservationInfoData));
                setLoading(false);
                return true;
            } catch (error) {
                console.error('Error preparing reservation edit:', error);
                setLoading(false);
                return false;
            }
        },
        [fetchTicketInfo],
    );

    return {
        isLoading,
        reservationTicketList,
        reservationCalendar,
        holidays,
        fetchReservationTickets,
        fetchReservationCalendar,
        fetchHolidays,
        prepareReservationEdit,
    };
};

export default useReservationTicket;
